import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import Page from '../../components/Page/Page';
import { NetworkContext } from '../../context/NetworkContext';
import { SnackBarContext } from '../../context/SnackbarContext';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  IconButton,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';

const emptyForm = {
  id: null,
  code: '',
  description: '',
  discount_type: 'percent',
  discount_value: '',
  valid_from: '',
  valid_until: '',
  max_uses: '',
  max_uses_per_user: 1,
  applicable_plans: [],
  status: true,
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  filter: {
    minWidth: 180,
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  hint: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
}));

export const SubscriptionCoupons = withRouter(() => {
  const classes = useStyles();
  const { sendNetworkRequest } = React.useContext(NetworkContext);
  const snackbarCtx = React.useContext(SnackBarContext);
  const [coupons, setCoupons] = useState([]);
  const [plans, setPlans] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  async function loadCoupons() {
    const result = await sendNetworkRequest('/admin/listsubscriptioncoupons', {}, {
      status: statusFilter === 'all' ? null : statusFilter,
    }, false);
    setCoupons(result?.coupons || []);
  }

  async function loadPlans() {
    const result = await sendNetworkRequest('/admin/listsubscriptionplans', {}, {}, false);
    setPlans(result?.plans || []);
  }

  function notify(result, fallback) {
    snackbarCtx.setSnack({
      ...snackbarCtx,
      open: true,
      type: result?.status === 'success' || result?.statusCode === 200 ? 'success' : (result?.status || 'error'),
      msg: result?.message || fallback,
      vertical: 'top',
    });
  }

  function openCreate() {
    setForm(emptyForm);
    setOpen(true);
  }

  function openEdit(coupon) {
    const selected = (coupon.applicable_plan_ids || [])
      .map(id => plans.find(p => p.id === id))
      .filter(Boolean);
    setForm({
      id: coupon.id,
      code: coupon.code || '',
      description: coupon.description || '',
      discount_type: coupon.discount_type || 'percent',
      discount_value: coupon.discount_value != null ? coupon.discount_value : '',
      valid_from: coupon.valid_from ? moment(coupon.valid_from).format('YYYY-MM-DD') : '',
      valid_until: coupon.valid_until ? moment(coupon.valid_until).format('YYYY-MM-DD') : '',
      max_uses: coupon.max_uses != null ? coupon.max_uses : '',
      max_uses_per_user: coupon.max_uses_per_user != null ? coupon.max_uses_per_user : '',
      applicable_plans: selected,
      status: coupon.status !== false,
    });
    setOpen(true);
  }

  async function saveCoupon() {
    const result = await sendNetworkRequest('/admin/savesubscriptioncoupon', {}, {
      id: form.id,
      code: form.code,
      description: form.description,
      discount_type: form.discount_type,
      discount_value: form.discount_value,
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
      max_uses: form.max_uses === '' ? null : form.max_uses,
      max_uses_per_user: form.max_uses_per_user === '' ? null : form.max_uses_per_user,
      applicable_plan_ids: (form.applicable_plans || []).map(p => p.id),
      status: form.status,
    }, false);
    notify(result, 'Unable to save coupon');
    if (result?.status === 'success') {
      setOpen(false);
      loadCoupons();
    }
  }

  async function toggleCoupon(coupon) {
    const result = await sendNetworkRequest('/admin/togglesubscriptioncoupon', {}, {
      id: coupon.id,
      status: !coupon.status,
    }, false);
    notify(result, 'Unable to update coupon status');
    if (result?.status === 'success') {
      loadCoupons();
    }
  }

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    loadCoupons();
  }, [statusFilter]);

  return (
    <Page className={classes.root} title="Membership Coupons">
      <div className={classes.header}>
        <Typography variant="h4">Membership Coupons</Typography>
        <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Coupon
        </Button>
      </div>
      <FormControl variant="outlined" className={classes.filter}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          label="Status"
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </Select>
      </FormControl>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Validity</TableCell>
              <TableCell>Max uses</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.map(row => (
              <TableRow key={row.id}>
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.discount_type === 'fixed' ? 'Fixed' : 'Percent'}</TableCell>
                <TableCell>
                  {row.discount_type === 'fixed' ? `₹${row.discount_value}` : `${row.discount_value}%`}
                </TableCell>
                <TableCell>
                  {(row.valid_from ? moment(row.valid_from).format('DD/MM/YYYY') : 'Open')
                    + ' – '
                    + (row.valid_until ? moment(row.valid_until).format('DD/MM/YYYY') : 'Open')}
                </TableCell>
                <TableCell>
                  {row.max_uses != null ? row.max_uses : 'Unlimited'}
                  {row.max_uses_per_user != null ? ` / ${row.max_uses_per_user} per user` : ''}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status ? 'Active' : 'Inactive'}
                    color={row.status ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(row)}>
                    <EditIcon />
                  </IconButton>
                  <Button size="small" onClick={() => toggleCoupon(row)}>
                    {row.status ? 'Deactivate' : 'Activate'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {coupons.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No membership coupons found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.id ? 'Edit coupon' : 'Create coupon'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.hint}>
            Leave dates empty for open-ended validity. Leave applicable plans empty to allow all plans. Leave max uses empty for unlimited.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Code"
                variant="outlined"
                fullWidth
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Discount type</InputLabel>
                <Select
                  value={form.discount_type}
                  onChange={e => setForm({ ...form, discount_type: e.target.value })}
                  label="Discount type"
                >
                  <MenuItem value="percent">Percent</MenuItem>
                  <MenuItem value="fixed">Fixed (INR)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={form.discount_type === 'fixed' ? 'Discount value (INR)' : 'Discount value (%)'}
                type="number"
                variant="outlined"
                fullWidth
                value={form.discount_value}
                onChange={e => setForm({ ...form, discount_value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.status}
                    onChange={e => setForm({ ...form, status: e.target.checked })}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Valid from"
                type="date"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.valid_from}
                onChange={e => setForm({ ...form, valid_from: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Valid until"
                type="date"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.valid_until}
                onChange={e => setForm({ ...form, valid_until: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max uses (global)"
                type="number"
                variant="outlined"
                fullWidth
                value={form.max_uses}
                onChange={e => setForm({ ...form, max_uses: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max uses per user"
                type="number"
                variant="outlined"
                fullWidth
                value={form.max_uses_per_user}
                onChange={e => setForm({ ...form, max_uses_per_user: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={plans}
                getOptionLabel={option => option.name || ''}
                getOptionSelected={(option, value) => option.id === value.id}
                value={form.applicable_plans}
                onChange={(_, value) => setForm({ ...form, applicable_plans: value })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.name} {...getTagProps({ index })} />
                  ))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Applicable plans"
                    helperText="Leave empty to apply to all plans"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={saveCoupon}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
});

export default SubscriptionCoupons;
